import { useEffect, useState } from "react";
import { startOfWeek, format } from "date-fns";

import useData from "./useData";

const useSyndication = () => {
  const { getFromServer, executeAction, sortByFields } = useData();

  const [syndication, setSyndication] = useState(null);
  const [syndicatedProjects, setSyndicatedProjects] = useState(null);
  const [syndicatedCalls, setSyndicatedCalls] = useState(null);
  const [syndicationList, setSyndicationList] = useState([]);

  useEffect(() => {
    getSyndication();
  }, []);

  const getSyndication = async () => {
    const results = await getFromServer({
      entityName: "Syndication",
      filter: { code: "main" },
    });

    if (results && results.length > 0) {
      setSyndication(results[0]);
      setSyndicationList(results);
      return results;
    }

    setSyndication(null);
    setSyndicationList([]);
    return [];
  };

  const getSyndicatedProjects = async () => {
    const results = await getFromServer({
      entityName: "Project",
      pipeline: "full",
      filter: { syndicationId: syndication.id, isActive: true },
    });

    if (results) {
      setSyndicatedProjects(results);
    } else {
      setSyndicatedProjects([]);
    }
  };

  const getSyndicatedCalls = async (filter) => {
    setSyndicatedCalls(null);
    const results = await executeAction({
      entityName: "Call",
      action: "fullList",
      data: { filter },
    });

    const payload = results?.isSuccessful ? results.data : [];

    if (Array.isArray(payload) && payload.length) {
      buildSyndicatedCalls(payload);
    } else {
      setSyndicatedCalls([]);
    }
  };

  const buildSyndicatedCalls = (results) => {
    if (!Array.isArray(results)) {
      setSyndicatedCalls([]);
      return;
    }

    // Convert each call into a docket - no grouping
    let dockets = results.map((call) => {
      return {
        _id: call._id,
        isSyndicated: call.syndicationId ? true : false,
        project: call.project,
        location: call.location,
        weekStart: startOfWeek(call.scheduleDate),
        scheduleDate: call.scheduleDate,
        repName: call.repName,
        territory: call.territory,
        callStatus: call.callStatus,
        calls: [call], // Each docket has exactly one call
      };
    });

    dockets = sortByFields(dockets, ["scheduleDate"]);

    setSyndicatedCalls(dockets);
  };

  const getDocketStatus = (docket) => {
    let status = "complete";
    for (const call of docket.calls) {
      if (call.callStatus === "incomplete") {
        status = "incomplete";
        break;
      }
    }
    return status;
  };

  const getSyndicatedTerritories = async () => {
    const results = await getFromServer({
      entityName: "Territory",
      filter: {
        syndicationId: { $ne: null },
      },
    });

    return results;
  };

  const getSyndicatedBanners = async () => {
    const results = await executeAction({
      entityName: "Syndication",
      action: "syndicatedBanners",
    });

    return results?.isSuccessful ? results.data : [];
  };

  return {
    syndication,
    syndicationList,
    getSyndicatedProjects,
    syndicatedProjects,
    getSyndicatedCalls,
    syndicatedCalls,
    getSyndicatedTerritories,
    getSyndicatedBanners,
    getDocketStatus,
  };
};

export default useSyndication;
